import { Offer } from "./API";
import { normalizeStoreKey } from "./seo";

export interface PartnerBoostBrand {
  mcid: string;
  mid: string;
  brand_id: string;
  merchant_name: string;
  comm_rate: string;
  comm_detail: string;
  site_url: string;
  logo: string;
  categories: string;
  tags: string;
  offer_type: string;
  brand_type: string;
  tracking_url: string | null;
  site_desc?: string;
}

interface PartnerBoostResponse {
  status: {
    code: number;
    msg: string;
  };
  data: {
    total_mcid: string;
    total_page: number;
    limit: number;
    list: PartnerBoostBrand[];
  };
}

export const PARTNERBOOST_SOURCE = "partnerboost";

const findBrandInList = (list: PartnerBoostBrand[], brandId: string) =>
  list.find(
    (brand) =>
      brand.brand_id === brandId ||
      brand.mid === brandId ||
      brand.mcid === brandId,
  );

const fetchPartnerBoostList = async (
  brandType?: string,
): Promise<PartnerBoostBrand[]> => {
  if (!brandType) {
    return [];
  }

  try {
    const params = new URLSearchParams({ brand_type: brandType });
    const res = await fetch(`/api/partnerboost?${params.toString()}`);
    if (!res.ok) {
      throw new Error(`Request failed: ${res.status} ${res.statusText}`);
    }

    const json = (await res.json()) as PartnerBoostResponse;
    if (json.status?.code !== 0 || !Array.isArray(json.data?.list)) {
      throw new Error(json.status?.msg || "Unexpected PartnerBoost response");
    }

    return json.data.list;
  } catch (error) {
    console.error("Failed to fetch PartnerBoost brands:", error);
    return [];
  }
};

export const brandTypeFromRoute = (category: string): string | null => {
  const map: Record<string, string> = {
    dtc: "DTC",
    walmart: "Walmart",
    amazon: "Amazon",
  };
  return map[category.toLowerCase()] ?? null;
};

export const brandTitleFromRoute = (category: string): string => {
  const map: Record<string, string> = {
    dtc: "DTC",
    walmart: "Walmart",
    amazon: "Amazon",
  };
  return (
    map[category.toLowerCase()] ??
    category.charAt(0).toUpperCase() + category.slice(1)
  );
};

const extractStore = (siteUrl: string) => {
  const normalized = normalizeStoreKey(siteUrl);
  return normalized.split("/")[0] || siteUrl;
};

export const mapPartnerBoostBrandToOffer = (
  brand: PartnerBoostBrand,
): Offer => {
  const store = extractStore(brand.site_url);

  return {
    offer_id: brand.brand_id || brand.mid,
    title: brand.comm_detail,
    description: brand.site_desc || brand.comm_detail || "",
    label: brand.comm_rate || "",
    code: "",
    featured: "No",
    source: PARTNERBOOST_SOURCE,
    deeplink: brand.tracking_url || brand.site_url || "",
    affiliate_link: brand.tracking_url || "",
    cashback_link: "",
    url: brand.site_url || "",
    image_url: brand.logo || "",
    brand_logo: brand.logo || "",
    type: brand.offer_type || "CPS",
    store,
    merchant_home_page: brand.site_url || "",
    merchant_name: brand.merchant_name,
    categories: brand.categories || "",
    standard_categories: brand.brand_type || "",
    start_date: null,
    end_date: null,
    status: "new",
    primary_location: "",
    locations: "",
    language: "",
    rating: "",
  };
};

export const getPartnerBoostBrands = async (
  brandType: string,
): Promise<{ result: boolean; brands: Offer[] }> => {
  const list = await fetchPartnerBoostList(brandType);
  return {
    result: list.length > 0,
    brands: list.map(mapPartnerBoostBrandToOffer),
  };
};

export const getPartnerBoostBrandById = async (
  brandId: string,
  brandType?: string | null,
): Promise<{ result: boolean; brand: Offer | null; relatedBrands: Offer[] }> => {
  if (!brandId) {
    return { result: false, brand: null, relatedBrands: [] };
  }

  const typesToSearch = brandType
    ? [brandType]
    : ["DTC", "Walmart", "Amazon"];

  for (const type of typesToSearch) {
    const list = await fetchPartnerBoostList(type);
    const match = findBrandInList(list, brandId);
    if (!match) continue;

    const brand = mapPartnerBoostBrandToOffer(match);
    const relatedBrands = list
      .filter((item) => item.brand_id !== match.brand_id)
      .map(mapPartnerBoostBrandToOffer)
      .slice(0, 5);

    return { result: true, brand, relatedBrands };
  }

  return { result: false, brand: null, relatedBrands: [] };
};
